import { getShopifyConfig } from "./config";

const SHOPIFY_STOREFRONT_API_VERSION = "2026-04";

export interface ShopifyGraphQLError {
  message: string;
  path?: Array<string | number>;
  extensions?: Record<string, unknown>;
}

export interface ShopifyUserError {
  field?: string[] | null;
  message: string;
  code?: string | null;
}

interface ShopifyGraphQLResponse<TData> {
  data?: TData;
  errors?: ShopifyGraphQLError[];
}

export interface ShopifyStorefrontRequestOptions<TVariables> {
  query: string;
  variables?: TVariables;
  operationName?: string;
  cache?: RequestCache;
  fetch?: typeof fetch;
}

export class ShopifyStorefrontTransportError extends Error {
  constructor(
    message: string,
    readonly details: {
      status?: number;
      responseBody?: string;
      cause?: unknown;
    } = {},
  ) {
    super(message, details.cause ? { cause: details.cause } : undefined);
    this.name = "ShopifyStorefrontTransportError";
  }
}

export class ShopifyStorefrontGraphQLError extends Error {
  constructor(
    readonly errors: ShopifyGraphQLError[],
    readonly status?: number,
  ) {
    super(
      `Shopify Storefront GraphQL error: ${errors.map((error) => error.message).join("; ")}`,
    );
    this.name = "ShopifyStorefrontGraphQLError";
  }
}

export class ShopifyStorefrontUserError extends Error {
  constructor(
    message: string,
    readonly userErrors: ShopifyUserError[],
  ) {
    super(`${message}: ${userErrors.map((error) => error.message).join("; ")}`);
    this.name = "ShopifyStorefrontUserError";
  }
}

function getShopifyStorefrontUrl(storeDomain: string): string {
  return `https://${storeDomain}/api/${SHOPIFY_STOREFRONT_API_VERSION}/graphql.json`;
}

export async function shopifyStorefrontRequest<
  TData,
  TVariables extends Record<string, unknown> | undefined = undefined,
>({
  query,
  variables,
  operationName,
  cache = "no-store",
  fetch: customFetch = fetch,
}: ShopifyStorefrontRequestOptions<TVariables>): Promise<TData> {
  const config = getShopifyConfig();
  const requestBody = JSON.stringify({
    query,
    variables,
    operationName,
  });

  let response: Response;

  try {
    response = await customFetch(getShopifyStorefrontUrl(config.storeDomain), {
      method: "POST",
      cache,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Shopify-Storefront-Access-Token": config.storefrontAccessToken,
      },
      body: requestBody,
    });
  } catch (error) {
    throw new ShopifyStorefrontTransportError(
      "Shopify Storefront request failed before a response was received.",
      { cause: error },
    );
  }

  const responseText = await response.text();

  if (!response.ok) {
    throw new ShopifyStorefrontTransportError(
      `Shopify Storefront request failed with HTTP ${response.status}.`,
      {
        status: response.status,
        responseBody: responseText,
      },
    );
  }

  let payload: ShopifyGraphQLResponse<TData>;

  try {
    payload = JSON.parse(responseText) as ShopifyGraphQLResponse<TData>;
  } catch (error) {
    throw new ShopifyStorefrontTransportError(
      "Shopify Storefront response was not valid JSON.",
      {
        status: response.status,
        responseBody: responseText,
        cause: error,
      },
    );
  }

  if (payload.errors?.length) {
    throw new ShopifyStorefrontGraphQLError(payload.errors, response.status);
  }

  if (payload.data === undefined) {
    throw new ShopifyStorefrontTransportError(
      "Shopify Storefront response did not include a data payload.",
      {
        status: response.status,
        responseBody: responseText,
      },
    );
  }

  return payload.data;
}

export function assertNoShopifyUserErrors(
  userErrors: ShopifyUserError[] | undefined,
  message: string,
): void {
  if (!userErrors?.length) {
    return;
  }

  throw new ShopifyStorefrontUserError(message, userErrors);
}
